import { useEffect, useMemo, useRef } from "react";
import { completeRunningTask, runningEntry, runningTask, startTimer, stopTimer, type Dataset } from "@otrack/domain";
import { projectLabel, projectRefs, targetLabel } from "./commands";
import type { AppContextValue, DesktopTimerState } from "./context";
import { navigate } from "./router";
import { quickStarts } from "./status-cards";
import { formatDuration } from "./time";

const RECENT_PROJECTS = 5;

/** The tray's view of the data: what runs, what the last entry was, and recent Projects. */
export function desktopTimerState(dataset: Dataset): DesktopTimerState {
  const running = runningEntry(dataset);
  const resume = running ? undefined : quickStarts(dataset).find((item) => item.kind === "resume");
  const resumeLabel = resume ? targetLabel(dataset, resume.projectId, resume.taskId) : "";
  return {
    running: running ? { label: projectLabel(dataset, running.projectId), startedAt: running.start, task: runningTask(dataset)?.name ?? null } : null,
    resume: resume ? { label: resume.description ? `${resumeLabel}, ${resume.description}` : resumeLabel } : null,
    recent: projectRefs(dataset).slice(0, RECENT_PROJECTS).map((ref) => ({ projectId: ref.id, label: projectLabel(dataset, ref.id) }))
  };
}

/**
 * Connects the shell to the desktop app: keeps the tray up to date and carries out what the tray,
 * the menu, and the global shortcuts ask for, the same way the Island and Today would.
 */
export function useDesktopIntegration(dataset: Dataset, context: AppContextValue): void {
  const desktop = context.services.desktop;
  const timer = useMemo(() => desktopTimerState(dataset), [dataset]);
  const published = useRef("");

  useEffect(() => {
    if (!desktop) return;
    const key = JSON.stringify(timer);
    if (key === published.current) return;
    published.current = key;
    desktop.publishTimer(timer);
  }, [desktop, timer]);

  // Commands read the latest data and context without re-subscribing on every tick.
  const latest = useRef({ dataset, context });
  latest.current = { dataset, context };

  useEffect(() => {
    if (!desktop) return;
    return desktop.onCommand((command) => {
      const { dataset: data, context: app } = latest.current;
      const factory = app.store.getState().factory;
      const now = Date.now();
      const running = runningEntry(data);
      const stop = () => {
        if (running) void app.act(stopTimer(data, factory, now), `Stopped at ${formatDuration(now - running.start)}`);
      };
      const resume = () => {
        const last = quickStarts(data).find((item) => item.kind === "resume");
        if (last) void app.act(startTimer(data, { projectId: last.projectId, taskId: last.taskId, description: last.description }, factory, now), `Resumed ${targetLabel(data, last.projectId, last.taskId)}`);
        else app.sendToCommandBar("", false);
      };
      switch (command.type) {
        case "stop": return stop();
        case "done": {
          const task = runningTask(data);
          if (running && task) void app.act(completeRunningTask(data, factory, now), `Completed ${task.name} at ${formatDuration(now - running.start)}`);
          return;
        }
        case "resume": return resume();
        case "toggle": return running ? stop() : resume();
        case "start":
          if (!data.projects[command.projectId]) return;
          return void app.act(startTimer(data, { projectId: command.projectId, description: "" }, factory, now), `Started ${projectLabel(data, command.projectId)}`);
        case "command-bar": return app.sendToCommandBar("", false);
        case "go": return navigate(command.page === "calendar" ? { page: "calendar", view: null, date: null } : { page: command.page });
      }
    });
  }, [desktop]);
}
